import _ from 'lodash';
import { zIndexManager } from 'components/sharpr/zIndex/manager/zIndexManager';

/**
 * Class to handle getting and setting caret and selections in input and textarea elements
 */
export class CaretManagerInput {
	/**
	 * Create an instance to manage the given input element
	 * @param {Element} input  The element to manage
	 */
	constructor(input) {
		/**
		 * @type {Element} input  The input whose caret to manage
		 */
		this.input = input;
	}

	/**
	 * Get the selectionStart and selectionEnd properties of the input
	 * @returns {Number[]}  An array with start and end
	 */
	getSelectionOffset() {
		return [this.input.selectionStart, this.input.selectionEnd];
	}

	/**
	 * Get the start and end positions of the current word
	 * @returns {Number[]}  An array with start and end
	 */
	getWordOffset() {
		const text = this.input.value;
		const len = text.length;
		let start = this.input.selectionStart;
		let end = this.input.selectionEnd - 1;
		if (/\s/.test(this.input.value.substring(start - 1, start))) {
			start++;
			end++;
		}
		while (start-- > 0 && !/\s/.test(text.substring(start - 1, start)));
		while (end++ < len && !/\s/.test(text.substring(end - 1, end)));
		if (/\s/.test(text.substring(end - 1, end))) {
			end--;
		}
		if (start < 0) {
			start = 0;
		}
		if (end > len) {
			end = len;
		}
		return [start, end];
	}

	/**
	 * Get the currently selected text
	 * @returns {String}
	 */
	getSelection() {
		let [start, end] = this.getSelectionOffset();
		return this.input.value.substring(start, end);
	}

	/**
	 * Get the current word
	 * @returns {String}
	 */
	getWord() {
		let [start, end] = this.getWordOffset();
		return this.input.value.substring(start, end);
	}

	/**
	 * Set the current word
	 * @param {String} word  The text to insert into the textarea
	 */
	setWord(word) {
		let [start, end] = this.getWordOffset();
		this.input.value =
			this.input.value.substring(0, start) + word + ' ' + this.input.value.substring(end).replace(/^ /, '');
		this.input.selectionStart = this.input.selectionEnd = start + word.length + 1;
	}

	/**
	 * Get all information about current word
	 * @returns  {Object}              Details about the current word
	 * @property {Object} start        Information about selection start
	 * @property {Number} start.chars  Number of characters from start of input
	 * @property {Number} start.top    The number of pixels offset relative to the input's top
	 * @property {Number} start.left   The number of pixels offset relative to the input's left
	 * @property {Object} end          Information about selection end
	 * @property {Number} end.chars    Number of characters from start of input
	 * @property {Number} end.top      The number of pixels offset relative to the input's top
	 * @property {Number} end.left     The number of pixels offset relative to the input's left
	 */
	getWordRect() {
		let [start, end] = this.getWordOffset();
		return this._getRect(start, end);
	}

	getWordSuggestionPosition(relativeToElement) {
		let { top, left } = this.getWordRect();
		const thisRect = relativeToElement.getBoundingClientRect();
		top -= thisRect.top;
		top += parseFloat(window.getComputedStyle(relativeToElement).getPropertyValue('font-size'));
		top += 5; // a little room between text and mention box
		left -= thisRect.left;
		return {
			top: top + 'px',
			left: left + 'px',
		};
	}

	/**
	 * Get all information about current selection
	 * @returns {Object}
	 * @returns {Object} start        Information about selection start
	 * @returns {Number} start.chars  Number of characters from start of input
	 * @returns {Number} start.top    The number of pixels offset relative to the input's top
	 * @returns {Number} start.left   The number of pixels offset relative to the input's left
	 * @returns {Object} end          Information about selection end
	 * @returns {Number} end.chars    Number of characters from start of input
	 * @returns {Number} end.top      The number of pixels offset relative to the input's top
	 * @returns {Number} end.left     The number of pixels offset relative to the input's left
	 */
	getSelectionRect() {
		let [start, end] = this.getSelectionOffset();
		return this._getRect(start, end);
	}

	_getRect(start, end) {
		let rect = this._calculateRect(start, end);
		let fontSize = parseFloat(window.getComputedStyle(this.input, null).getPropertyValue('font-size')) || 0;
		return {
			top: rect.top,
			left: rect.left,
			width: rect.width,
			height: rect.height,
			start: {
				offset: start,
				top: rect.top + fontSize - this.input.scrollTop,
				left: rect.left,
			},
			end: {
				offset: end,
				top: rect.top + (start === end ? 0 : rect.height) + fontSize - this.input.scrollTop,
				left: rect.left + (start === end ? 0 : rect.width),
			},
		};
	}

	/**
	 * Create a shadow element to help calculate selection size and position
	 * @private
	 */
	_createShadow() {
		let div = document.createElement('div');
		let rect = this.input.getBoundingClientRect();
		this._copyStyles(this.input, div);
		_.extend(div.style, {
			position: 'absolute',
			color: 'red',
			backgroundColor: '#eee',
			top: rect.top + (window.scrollY || window.pageYOffset) + 'px',
			left: rect.left + (window.scrollX || window.pageXOffset) + 'px',
			wordWrap: 'break-word',
			overflowX: 'hidden',
			overflowY: 'auto',
			whiteSpace: 'pre-wrap',
			zIndex: '-1',
			visibility: 'hidden',
		});
		document.body.appendChild(div);
		this.input.caretShadow = div;
	}

	/**
	 * Copy all the CSS properties that affect input rendering
	 * @param {Element} sourceNode  The node from which to copy
	 * @param {Element} targetNode  The node to which to copy
	 * @private
	 */
	_copyStyles(sourceNode, targetNode) {
		let styles = window.getComputedStyle(sourceNode, null);
		this._getRelevantCssProps().forEach(prop => {
			targetNode.style[prop] = styles.getPropertyValue(prop);
		});
	}

	/**
	 * Get the input's shadow element, creating it first if needed
	 * @returns {Element}
	 * @private
	 */
	_getInputShadow() {
		if (!this.input.caretShadow) {
			this._createShadow();
		}
		return this.input.caretShadow;
	}

	/**
	 * Get the bounding client rect of current text selection
	 * @param start  The selection start point
	 * @param end    The selection end point
	 * @returns {ClientRect}  With properties top, left, width, height
	 * @private
	 */
	_calculateRect(start, end) {
		let shadow = this._getInputShadow();
		shadow.innerHTML =
			_.escape(this.input.value.substring(0, start)).replace(/\n/g, '<br>') +
			'<span>' +
			_.escape(this.input.value.substring(start, end)) +
			'&#8203;</span>';
		return shadow.querySelector('span').getBoundingClientRect();
	}

	/**
	 * Draw a rectangle around the selected text
	 * for debugging purposes
	 * @param {String} borderColor  A CSS string for color
	 * @private
	 */
	paintSelectionRect(borderColor = 'red') {
		let [start, end] = this.getSelectionOffset();
		this._paintRect(start, end, 'caretSelectionRect', borderColor);
	}

	/**
	 * Draw a rectangle around the current word or words
	 * for debugging purposes
	 * @param {String} borderColor  A CSS string for color
	 * @private
	 */
	paintWordRect(borderColor = 'blue') {
		let [start, end] = this.getWordOffset();
		this._paintRect(start, end, 'caretWordRect', borderColor);
	}

	/**
	 * Draw a rectangle around the given start and end offset
	 * @param {Number} start  The start offset
	 * @param {Number} end  The end offset
	 * @param {String} expando  The name of the expando property to use
	 * @param {String} borderColor  A CSS string for color
	 * @private
	 */
	_paintRect(start, end, expando, borderColor) {
		let rect = this._calculateRect(start, end);
		if (!this.input[expando]) {
			this.input[expando] = document.createElement('div');
			document.body.appendChild(this.input[expando]);
			_.extend(this.input[expando].style, {
				position: 'absolute',
				zIndex: zIndexManager.next(),
				transition: 'all 125ms ease-in',
			});
		}
		_.extend(this.input[expando].style, {
			top: rect.top - this.input.scrollTop + 'px',
			left: rect.left + 'px',
			width: rect.width + 'px',
			height: rect.height + 'px',
			border: '1px solid ' + borderColor,
		});
	}

	/**
	 * Get an array of all the CSS Properties that can affect the
	 * display of the text box
	 * @returns {String[]}
	 * @private
	 */
	_getRelevantCssProps() {
		return [
			'border',
			'box-sizing',
			'break-after',
			'break-before',
			'break-inside',
			'clip',
			'content',
			'font-family',
			'font-kerning',
			'font-size',
			'font-stretch',
			'font-style',
			'font-variant',
			'font-variant-ligatures',
			'font-weight',
			'height',
			'letter-spacing',
			'line-height',
			'outline',
			'overflow-wrap',
			'overflow',
			'padding',
			'position',
			'table-layout',
			'tab-size',
			'text-align',
			'text-align-last',
			'text-decoration',
			'text-indent',
			'text-rendering',
			'text-shadow',
			'text-overflow',
			'text-transform',
			'unicode-bidi',
			'vertical-align',
			'visibility',
			'white-space',
			'widows',
			'width',
			'word-break',
			'word-spacing',
			'word-wrap',
			'zoom',
			'align-content',
			'align-items',
			'align-self',
			'flex-basis',
			'flex-grow',
			'flex-shrink',
			'flex-direction',
			'flex-wrap',
			'justify-content',
			'perspective',
			'transform',
		];
	}
}
